<template>
  <div
    v-if="getProfile && getProfile.isAdmin"
    class="panel is-danger gameAdmin p-0"
  >
    <p class="panel-heading">Admin Panel</p>

    <section class="is-flex is-justify-content-space-between my-5 mx-3">
      <b-field>
        <b-input
          v-model="filterText"
          placeholder="Type to search for tournaments ..."
          type="search"
          icon="search"
        ></b-input>
      </b-field>

      <b-field label="status" label-position="on-border" type="is-info">
        <b-select v-model="filterStatus">
          <option value="NotEnded" key="NotEnded">Not Ended</option>
          <option value="Coming" key="Coming">Coming</option>
          <option value="Live" key="Live">Live</option>
          <option value="Ended" key="Ended">Ended</option>
          <option value="All" key="All">All</option>
        </b-select>
      </b-field>

      <b-field label="Privacy" label-position="on-border" type="is-info">
        <b-select v-model="filterPrivacy">
          <option value="" key="privacyAll">All</option>
          <option value="Private" key="privacyPrivate">Private</option>
          <option value="Invite" key="privacyInvite">Invite</option>
          <option value="Paid" key="privacyPaid">Paid</option>
          <option value="Open" key="privacyOpen">Open</option>
          <option value="Password" key="privacyPassword">Password</option>
        </b-select>
      </b-field>

      <b-button
        label="Create New"
        type="is-primary"
        size="is-medium"
        @click="createTournament"
      />
    </section>

    <b-modal
      v-model="isComponentModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      :can-cancel="false"
    >
      <template #default="props">
        <TournamentForm :tournament="formProps" @close="props.close" />
      </template>
    </b-modal>

    <table
      aria-label="List of tournaments"
      v-if="availableTournaments.length"
      style="width: 100%; text-align: left"
      class="table"
    >
      <thead>
        <tr>
          <th style="width: 30%">Tournament</th>
          <th style="width: 15%">Start Time</th>
          <th style="width: 15%">Time Control</th>
          <th style="width: 5%">Players</th>
          <th style="width: 5%">Rounds</th>
          <th style="width: 5%">Type</th>
          <th style="width: 5%">Privacy</th>
          <th style="width: 5%">Round mode</th>
          <th style="width: 5%">Status</th>
          <th style="width: 10%">Action</th>
        </tr>
      </thead>
      <tbody
        v-for="(tournament, t) in availableTournaments"
        :key="tournament.id"
      >
        <tr
          :class="t % 2 === 0 ? 'even' : 'odd'"
          :style="tournament | rowStyle"
        >
          <td>
            <router-link
              :to="`/admin/tournaments/${tournament.id}`"
              class="has-text-info"
            >
              {{ tournament.name }}
              <span
                v-if="tournament.recurrence && tournament.recurrence.repeat"
              >
                {{
                  tournament.startTime
                    | asDayjsFormat(
                      `DD/MM/YYYY${
                        tournament.recurrence.repeat.basis === "minute"
                          ? " HH:mm"
                          : ""
                      }`
                    )
                }}
              </span>
            </router-link>
          </td>
          <td>
            {{ tournament.startTime | asDate }}
            <br />
            {{ tournament.startTime | asTime }}
          </td>
          <td>
            <p>{{ tournament.baseTimeControl }} minutes</p>
            <p>+{{ tournament.increment }}s per move</p>
          </td>
          <td>
            {{ tournament.playerCount ? tournament.playerCount : 0 }}/{{
              tournament.playerLimit ? tournament.playerLimit : "∞"
            }}
          </td>
          <td>
            {{ tournament.currentRound }}/{{ tournament.totalRounds }}
            {{ tournament.automate ? "🤖" : "" }}
          </td>
          <td>{{ tournament.type }}</td>
          <td>{{ tournament.privacy }}</td>
          <td>{{ tournament.roundMode }}</td>
          <td>
            {{ tournament | statusText }}
          </td>
          <td>
            <button
              class="button is-small is-fullwidth is-primary"
              @click="editTournament(tournament)"
            >
              Edit
            </button>
            <button
              class="button is-small is-fullwidth is-danger"
              @click="deleteTournament(tournament)"
            >
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { firestore } from "@/services/firebase";
import { mapGetters } from "vuex";
import TournamentPermission from "@/components/admins/TournamentPermission";

const tournamentsRef = firestore.collection("tournaments");

export default {
  name: "AdminTournaments",
  title: "Admin Tournaments",

  data() {
    return {
      tournaments: [],
      filterText: "",
      filterStatus: "NotEnded",
      filterPrivacy: "",
      isInitialized: false, // To prevent initial watch triggers
    };
  },

  created() {
    // Initialize filters from URL
    const query = this.$route.query;
    this.filterStatus = query.status || "NotEnded";
    this.filterPrivacy = query.privacy || "";
    this.isInitialized = true;
  },

  mixins: [TournamentPermission],

  watch: {
    // Watch individual filters
    filterStatus() {
      if (!this.isInitialized) return;
      this.updateQueryParams();
    },
    filterPrivacy() {
      if (!this.isInitialized) return;
      this.updateQueryParams();
    },

    // Watch URL changes
    "$route.query": {
      handler(newQuery) {
        if (!this.isInitialized) return;

        // Only update if values are different to prevent loops
        if (newQuery.status !== this.filterStatus) {
          this.filterStatus = newQuery.status || "NotEnded";
        }
        if (newQuery.privacy !== this.filterPrivacy) {
          this.filterPrivacy = newQuery.privacy || "";
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapGetters(["getProfile"]),

    availableTournaments() {
      if (!this.tournaments) return [];
      let filtered = this.tournaments
        .filter((t) => !t.isDeleted)
        .sort((a, b) => b.startTime - a.startTime);

      if (this.filterText) {
        filtered = filtered.filter((t) =>
          t.name.toLowerCase().includes(this.filterText.toLowerCase())
        );
      }

      if (this.filterPrivacy) {
        filtered = filtered.filter((t) => t.privacy === this.filterPrivacy);
      }

      switch (this.filterStatus) {
        case "NotEnded":
          filtered = filtered.filter((t) => !(t.currentRound > t.totalRounds));
          break;
        case "Coming":
          filtered = filtered.filter((t) => t.currentRound === 0);
          break;
        case "Live":
          filtered = filtered.filter(
            (t) => t.currentRound > 0 && t.currentRound < t.totalRounds
          );
          break;
        case "Ended":
          filtered = filtered.filter((t) => t.currentRound > t.totalRounds);
          break;
        default:
          break;
      }

      return filtered;
    },
  },

  firestore: {
    tournaments: tournamentsRef.where("isDeleted", "==", false),
  },

  filters: {
    statusText(tournament) {
      if (tournament.currentRound > tournament.totalRounds) return "Ended";
      if (tournament.currentRound === 0) return "Coming";
      return "Live";
    },

    rowStyle(tournament) {
      return {
        "background-color":
          tournament.currentRound === 0
            ? "paleturquoise"
            : tournament.currentRound > tournament.totalRounds
            ? "white"
            : "palegreen",
      };
    },
  },

  methods: {
    updateQueryParams() {
      // Build query object
      const query = {};
      if (this.filterStatus) query.status = this.filterStatus;
      if (this.filterPrivacy) query.privacy = this.filterPrivacy;

      // Update URL without reloading the page
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            ...query,
          },
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
th,
td {
  vertical-align: middle !important;
}
</style>
